// REPORTS -- what the team has quoted and won. READ ONLY.
//
// Owner brief, 2026-09-23 (sales-report):
//   * ⚠️ SUPERSEDED BY quote-won-lost (2026-09-23): "Won" is now the quote's
//     outcome status = Won (a PO saved on a finalised quote sets it). The
//     page adds Lost / Open / Expired, the win rate (Won / (Won + Lost)) and
//     the lost reasons -- all computed server-side, printed as sent.
//   * (was) "Won" = a FINALISED quote with a customer PO recorded.
//   * For a date range (the quotes' own dates, inclusive): count and value of
//     drafts, finalised and won; GP % on finalised and won; breakdowns by
//     customer, by salesperson and by month.
//   * A quote that cannot price is COUNTED but kept out of value and GP, and
//     shown as "unpriced (n)".
//   * Admin sees everyone's; Sales sees only their own; a Viewer gets no page
//     (and the route refuses one).
//   * Internal, so buy-side figures could appear -- but there is NO export
//     and NO print in this slice.
//
// ⚠️ THIS PAGE FORMATS AND NOTHING ELSE. Every count, value and GP % comes
// from /anixify/api/sales_report, aggregated server-side from stored engine
// outputs. Adding two numbers here would be a second place a figure is
// decided -- the "Pricing calculations" hard rule in CLAUDE.md.

import { useEffect, useState } from "react";
import {
  downloadSalesReportXlsx, getSalesReport, type SalesBreakdownRow, type SalesBucket,
  type SalesLostReason, type SalesReport,
} from "@/services/api";
import { useCanSeeReportsOrFalse, useIsAdminOrFalse } from "@/auth/AuthContext";
import { formatPercent, formatRupees } from "@/lib/format";
import { colors, semanticColors, radius, spacing, heights, typography } from "@/styles/tokens";

/** Local calendar date as YYYY-MM-DD. */
function isoDate(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/** The default range: the current month to date. */
export function defaultRange(now: Date = new Date()): { from: string; to: string } {
  return { from: isoDate(new Date(now.getFullYear(), now.getMonth(), 1)), to: isoDate(now) };
}

const INPUT: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[8]}px`,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.base, backgroundColor: semanticColors.surface,
  color: semanticColors.textPrimary,
};

const CELL: React.CSSProperties = {
  padding: `${spacing[8]}px ${spacing[12]}px`, fontSize: typography.fontSize.sm,
  color: semanticColors.textPrimary, borderBottom: `1px solid ${semanticColors.border}`,
  whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums",
};
const HEAD: React.CSSProperties = {
  ...CELL, color: semanticColors.textMuted, fontWeight: typography.fontWeight.medium,
  fontSize: typography.fontSize.xs, backgroundColor: semanticColors.panelBg,
};

function gp(bucket: SalesBucket): string {
  return typeof bucket.gp_pct === "number" ? formatPercent(bucket.gp_pct) : "—";
}

/** "unpriced (n)", only when n > 0 -- an absent note means none. */
function Unpriced({ n, testid }: { n: number; testid: string }) {
  if (!n) return null;
  return (
    <span
      data-testid={testid}
      title="Counted, but kept out of value and GP: at least one line cannot price."
      style={{ marginLeft: spacing[6], color: colors.amber800, fontSize: typography.fontSize.xs }}
    >
      unpriced ({n})
    </span>
  );
}

function Card({ title, bucket, testid, showGp }: {
  title: string; bucket: SalesBucket; testid: string; showGp: boolean;
}) {
  return (
    <div
      data-testid={testid}
      style={{
        flex: "1 1 220px", padding: spacing[16], border: `1px solid ${semanticColors.border}`,
        borderRadius: radius.base, backgroundColor: semanticColors.surface,
      }}
    >
      <div style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>{title}</div>
      <div data-testid={`${testid}-count`} style={{
        fontSize: typography.fontSize.xxl, fontWeight: typography.fontWeight.semibold,
        color: semanticColors.textPrimary, fontVariantNumeric: "tabular-nums", marginTop: spacing[4],
      }}>
        {bucket.count}
        <Unpriced n={bucket.unpriced} testid={`${testid}-unpriced`} />
      </div>
      <div data-testid={`${testid}-value`} style={{
        fontSize: typography.fontSize.base, color: semanticColors.textPrimary,
        fontVariantNumeric: "tabular-nums", marginTop: spacing[4],
      }}>
        {formatRupees(bucket.value)}
      </div>
      {showGp && (
        <div data-testid={`${testid}-gp`} style={{
          fontSize: typography.fontSize.sm, color: semanticColors.textSecondary, marginTop: spacing[2],
        }}>
          GP {gp(bucket)}
        </div>
      )}
    </div>
  );
}

/** quote-won-lost: Won / (Won + Lost), as the server computed it. */
function WinRate({ pct }: { pct: number | null | undefined }) {
  return (
    <div
      data-testid="reports-win-rate"
      style={{
        flex: "1 1 220px", padding: spacing[16], border: `1px solid ${semanticColors.border}`,
        borderRadius: radius.base, backgroundColor: semanticColors.surface,
      }}
    >
      <div style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>Win rate</div>
      <div data-testid="reports-win-rate-value" style={{
        fontSize: typography.fontSize.xxl, fontWeight: typography.fontWeight.semibold,
        color: semanticColors.textPrimary, fontVariantNumeric: "tabular-nums", marginTop: spacing[4],
      }}>
        {typeof pct === "number" ? formatPercent(pct) : "—"}
      </div>
      <div style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary, marginTop: spacing[4] }}>
        Won ÷ (Won + Lost), by count
      </div>
    </div>
  );
}

/** quote-won-lost: Lost quotes by reason. Every reason is listed. */
function LostReasons({ rows }: { rows: SalesLostReason[] }) {
  return (
    <div style={{ marginTop: spacing[24] }}>
      <div style={{
        fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold,
        color: semanticColors.textPrimary, marginBottom: spacing[8],
      }}>
        Lost reasons
      </div>
      <div style={{ border: `1px solid ${semanticColors.border}`, borderRadius: radius.base, overflowX: "auto" }}>
        <table data-testid="reports-lost-reasons" style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...HEAD, textAlign: "left" }}>Reason</th>
              <th style={{ ...HEAD, textAlign: "right" }}>Lost</th>
              <th style={{ ...HEAD, textAlign: "right" }}>Value</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.reason} data-testid={`reports-lost-reason-${r.reason}`}>
                <td style={{ ...CELL, textAlign: "left" }}>{r.label}</td>
                <td style={{ ...CELL, textAlign: "right" }} data-testid={`reports-lost-reason-${r.reason}-count`}>
                  {r.count}
                </td>
                <td style={{ ...CELL, textAlign: "right" }} data-testid={`reports-lost-reason-${r.reason}-value`}>
                  {formatRupees(r.value)}
                  <Unpriced n={r.unpriced} testid={`reports-lost-reason-${r.reason}-unpriced`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Breakdown({ title, rows, testid, labelHeader }: {
  title: string; rows: SalesBreakdownRow[]; testid: string; labelHeader: string;
}) {
  const figure = (b: SalesBucket, cellId: string) => (
    <>
      <span data-testid={`${cellId}-count`}>{b.count}</span>
      {" · "}
      <span data-testid={`${cellId}-value`}>{formatRupees(b.value)}</span>
      <Unpriced n={b.unpriced} testid={`${cellId}-unpriced`} />
    </>
  );
  return (
    <div style={{ marginTop: spacing[24] }}>
      <div style={{
        fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold,
        color: semanticColors.textPrimary, marginBottom: spacing[8],
      }}>
        {title}
      </div>
      <div style={{ border: `1px solid ${semanticColors.border}`, borderRadius: radius.base, overflowX: "auto" }}>
        <table data-testid={testid} style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...HEAD, textAlign: "left" }}>{labelHeader}</th>
              <th style={{ ...HEAD, textAlign: "right" }}>Drafts</th>
              <th style={{ ...HEAD, textAlign: "right" }}>Finalised</th>
              <th style={{ ...HEAD, textAlign: "right" }}>GP %</th>
              <th style={{ ...HEAD, textAlign: "right" }}>Won</th>
              <th style={{ ...HEAD, textAlign: "right" }}>GP %</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} style={{ ...CELL, color: semanticColors.textSecondary }}>No quotes in this range.</td></tr>
            )}
            {rows.map((r) => {
              const id = `${testid}-${r.key}`;
              return (
                <tr key={r.key} data-testid={id}>
                  <td style={{ ...CELL, textAlign: "left" }}>{r.label}</td>
                  <td style={{ ...CELL, textAlign: "right" }}>{figure(r.drafts, `${id}-drafts`)}</td>
                  <td style={{ ...CELL, textAlign: "right" }}>{figure(r.finalised, `${id}-finalised`)}</td>
                  <td style={{ ...CELL, textAlign: "right" }} data-testid={`${id}-finalised-gp`}>{gp(r.finalised)}</td>
                  <td style={{ ...CELL, textAlign: "right" }}>{figure(r.won, `${id}-won`)}</td>
                  <td style={{ ...CELL, textAlign: "right" }} data-testid={`${id}-won-gp`}>{gp(r.won)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ReportsPage() {
  const canSee = useCanSeeReportsOrFalse();
  // EXPORT IS ADMIN ONLY (reports-export): the button is hidden for Sales and
  // the route refuses them regardless.
  const canExport = useIsAdminOrFalse();
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  const exportXlsx = async () => {
    setExporting(true);
    setExportError("");
    const result = await downloadSalesReportXlsx(from, to);
    setExporting(false);
    if (!result.ok || !result.blob) {
      setExportError(result.error ?? "The export failed.");
      return;
    }
    // Save it: a temporary link to the file, clicked once, then released.
    if (typeof URL.createObjectURL === "function") {
      const url = URL.createObjectURL(result.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = result.filename ?? "sales-report.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    }
  };
  const initial = defaultRange();
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [report, setReport] = useState<SalesReport | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!canSee) return;
    let live = true;
    setLoading(true);
    setError("");
    void getSalesReport(from, to).then((result) => {
      if (!live) return;
      setLoading(false);
      if (!result.ok || !result.report) {
        // FAIL LOUD: clear the figures rather than leave the previous range's
        // numbers on screen under the new dates.
        setReport(null);
        setError(result.error ?? "The report could not be read.");
        return;
      }
      setReport(result.report);
    });
    return () => { live = false; };
  }, [canSee, from, to]);

  if (!canSee) {
    return (
      <div data-testid="reports-not-available" style={{ padding: spacing[24], color: semanticColors.textSecondary }}>
        Reports are available to Sales and Admin.
      </div>
    );
  }

  return (
    <div data-testid="reports-page" style={{ padding: spacing[24] }}>
      <div style={{ marginBottom: spacing[16] }}>
        <div data-testid="reports-title" style={{
          fontSize: typography.fontSize.xxl, fontWeight: typography.fontWeight.semibold,
          color: semanticColors.textPrimary, letterSpacing: typography.letterSpacing.tight,
        }}>
          Reports
        </div>
        <div data-testid="reports-subtitle" style={{
          fontSize: typography.fontSize.base, color: semanticColors.textSecondary, marginTop: spacing[4],
        }}>
          What {report?.scope === "all" ? "the team has" : "you have"} quoted and won, by quote date.
          Won and Lost are the status recorded on a finalised quote (a customer PO marks it
          Won); Expired is an Open quote past its Valid Until date. Values are the stored
          sell totals; a quote that cannot price is counted but left out of value and GP.
        </div>
      </div>

      <div style={{
        height: heights.filterBar, backgroundColor: semanticColors.panelBg,
        border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
        padding: `0 ${spacing[12]}px`, display: "flex", alignItems: "center", gap: spacing[12],
      }}>
        <label style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
          From{" "}
          <input data-testid="reports-from" type="date" value={from} max={to || undefined}
                 onChange={(e) => setFrom(e.target.value)} style={INPUT} />
        </label>
        <label style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
          To{" "}
          <input data-testid="reports-to" type="date" value={to} min={from || undefined}
                 onChange={(e) => setTo(e.target.value)} style={INPUT} />
        </label>
        <span data-testid="reports-scope" style={{ marginLeft: "auto", fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
          {report ? (report.scope === "all" ? "Everyone's quotes" : "Your quotes only") : ""}
        </span>
        {canExport && (
          <button
            data-testid="reports-export"
            onClick={() => { void exportXlsx(); }}
            disabled={exporting || !report}
            style={{
              height: heights.control, padding: `0 ${spacing[14]}px`,
              border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
              backgroundColor: semanticColors.surface, color: semanticColors.textSecondary,
              fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
              cursor: exporting || !report ? "not-allowed" : "pointer",
            }}
          >
            {exporting ? "Exporting…" : "Export to Excel"}
          </button>
        )}
      </div>
      {exportError !== "" && (
        <div data-testid="reports-export-error" role="alert" style={{
          marginTop: spacing[8], fontSize: typography.fontSize.sm, color: semanticColors.destructive,
        }}>
          {exportError}
        </div>
      )}

      {loading && !report && (
        <div data-testid="reports-loading" style={{ marginTop: spacing[16], color: semanticColors.textSecondary }}>
          Reading the report…
        </div>
      )}
      {error !== "" && (
        <div data-testid="reports-error" role="alert" style={{
          marginTop: spacing[16], padding: `${spacing[8]}px ${spacing[12]}px`,
          backgroundColor: semanticColors.destructiveBg, border: `1px solid ${semanticColors.destructiveBorder}`,
          color: semanticColors.destructive, borderRadius: radius.base, fontSize: typography.fontSize.sm,
        }}>
          {error}
        </div>
      )}

      {report && (
        <>
          <div style={{ display: "flex", gap: spacing[16], flexWrap: "wrap", marginTop: spacing[16] }}>
            <Card title="Drafts" bucket={report.totals.drafts} testid="reports-drafts" showGp={false} />
            <Card title="Finalised" bucket={report.totals.finalised} testid="reports-finalised" showGp />
            <Card title="Won" bucket={report.totals.won} testid="reports-won" showGp />
          </div>
          {/* quote-won-lost: the rest of finalised, and the win rate. Shown
              only when the backend sent them. */}
          {report.totals.lost && report.totals.open && report.totals.expired && (
            <div style={{ display: "flex", gap: spacing[16], flexWrap: "wrap", marginTop: spacing[16] }}>
              <Card title="Lost" bucket={report.totals.lost} testid="reports-lost" showGp />
              <Card title="Open" bucket={report.totals.open} testid="reports-open" showGp />
              <Card title="Expired" bucket={report.totals.expired} testid="reports-expired" showGp />
              {/* quote-revisions: replaced by a finalised revision -- in
                  neither Open nor Expired, and not in the win rate. */}
              {report.totals.superseded && (
                <Card title="Superseded" bucket={report.totals.superseded} testid="reports-superseded" showGp={false} />
              )}
              <WinRate pct={report.totals.win_rate_pct} />
            </div>
          )}
          {report.lost_reasons && <LostReasons rows={report.lost_reasons} />}
          <Breakdown title="By customer" rows={report.by_customer} testid="reports-by-customer" labelHeader="Customer" />
          <Breakdown title="By salesperson" rows={report.by_salesperson} testid="reports-by-salesperson" labelHeader="Salesperson" />
          <Breakdown title="By month" rows={report.by_month} testid="reports-by-month" labelHeader="Month" />
        </>
      )}
    </div>
  );
}
